import { atom, map } from "nanostores"

import { fetchData } from "utils/fetch-data"
import { useStore } from "./use-store"
import { $user, useUser } from "./user"

type Price = {
  unit_amount: number
  currency: string
  price_id: string
}

type Currency = {
  acronym: string
  prefix: string
}

type IsoCountry = {
  iso: string
  name: string
  currency: string
}

export type AuthProvider = {
  enabled: boolean
  client_id?: string
  redirect_uri?: string
  state?: string
  nonce?: string
}

export type AuthProviders = {
  apple?: AuthProvider
  google?: AuthProvider
}

export type Support = {
  stripe_publishable_key: string
  default_iso_country: string
  iso_countries: IsoCountry[]
  currencies: Currency[]
  auth_providers?: AuthProviders
  csrf_token?: string
  products: {
    annually: {
      prices: {
        [currency: string]: Price
      }
    }
    monthly: {
      prices: {
        [currency: string]: Price
      }
    }
  }
  user: {
    premium: boolean
    signedIn: boolean
    emailVerified: boolean
    email: string | null
    forcedCurrency: string | null
    premiumExpires: number | null
  }
  messages: {
    [key: string]: string
  }
}

export const $support = atom<Support | null>(null)

export const $supportLoadingStates = map<{
  fulfilled: boolean
  loading: boolean
  error: any | null
}>({
  fulfilled: false,
  loading: false,
  error: null,
})

let supportRequest: Promise<void> | null = null

async function loadSupport(): Promise<void> {
  try {
    $supportLoadingStates.setKey("loading", true)
    $supportLoadingStates.setKey("error", null)

    const data = await fetchData("/sign_up/support")

    $support.set(data)
    if (data.csrf_token) {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
      if (csrfMeta) csrfMeta.setAttribute("content", data.csrf_token)
    }
    $user.setKey("premium", data.user.premium)
    $user.setKey("signedIn", data.user.signedIn)
    $user.setKey("forcedCurrency", data.user.forcedCurrency)
    $user.setKey("email", data.user.email ?? undefined)

    $supportLoadingStates.setKey("fulfilled", true)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    // A failed refresh must not replace support that is already loaded: keep the
    // previous data (and its CSRF token) so sign-in can still continue to payment.
    if ($support.get() == null) $supportLoadingStates.setKey("error", error)
  } finally {
    $supportLoadingStates.setKey("loading", false)
  }
}

export function prefetchSupport(): Promise<void> {
  if (!supportRequest) {
    supportRequest = loadSupport().finally(() => {
      supportRequest = null
    })
  }
  return supportRequest
}

export function useSupport(): Support {
  const support = useStore($support)
  if (support == null) {
    throw new Error("$supportLoadingStates must be fulfilled before accessing useSupport")
  }

  return support
}

export function usePrices() {
  const support = useSupport()
  const { isoCountry, forcedCurrency } = useUser()

  let iso = "US"
  let currencyAcronym = "USD"
  if (forcedCurrency != null) {
    const country = support.iso_countries.find(({ currency }) => forcedCurrency === currency)
    if (country != null) {
      iso = country.iso
      currencyAcronym = country.currency
    }
  } else {
    const country = support.iso_countries.find(
      ({ iso: shadowIso }) => (isoCountry ?? support.default_iso_country) === shadowIso,
    )
    if (country != null) {
      iso = country.iso
      currencyAcronym = country.currency
    }
  }

  const currency = support.currencies.find(({ acronym }) => currencyAcronym === acronym)

  const monthly = support.products.monthly.prices[currencyAcronym]
  const annually = support.products.annually.prices[currencyAcronym]

  return { iso, currency, monthly, annually }
}
